import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

/**
 * GenericDynamicSection
 *
 * Renders any page section added from the super admin portal, dynamically,
 * based on the section's `display_type`. This ensures that ANY new section
 * added by the super admin automatically appears on the frontend without
 * requiring code changes.
 *
 * Supported display_types:
 *   grid, destination_grid                → image grid cards
 *   image_slider, destination_slider      → Swiper image slider
 *   offer_cards, package_cards            → styled offer/package cards
 *   benefits                              → icon + text benefits list
 *   banner, hero                          → full-width banner/hero
 *   banners                               → multi-banner row
 *   listing_heading                       → section heading + description
 *   cab_routes, cab_slider                → cab card grid/slider
 *   (default/fallback)                    → simple card grid
 */
const GenericDynamicSection = ({ section }) => {
    if (!section || section.status === 'inactive') return null;

    const items = section.Items || [];
    const title = section.title;
    const titleHighlight = section.title_highlight;
    const subtitle = section.subtitle;
    const description = section.description;
    const displayType = section.display_type || 'grid';
    const bgImage = section.background_image;

    const SectionHeader = () => (
        (title || titleHighlight || subtitle) ? (
            <div className="title">
                {(title || titleHighlight) && (
                    <h3>
                        {titleHighlight && <span>{titleHighlight} </span>}
                        {title || ''}
                    </h3>
                )}
                {subtitle && <p>{subtitle}</p>}
                {description && <p className="text-muted">{description}</p>}
            </div>
        ) : null
    );

    // Section-level CTA button (set via Button Text + Button Link in super admin)
    const SectionCTA = () =>
        section.button_text ? (
            <div className="text-center mt-3 mb-2">
                <a
                    href={section.button_link || '#'}
                    className="btn btn-primary px-4"
                    rel="noreferrer"
                >
                    {section.button_text}
                </a>
            </div>
        ) : null;

    /* ─── GRID / DESTINATION GRID ─── */
    if (displayType === 'grid' || displayType === 'destination_grid') {
        return (
            <div className="container swiper pb-5 holidays-left">
                <div className="popular-state pb-0 pt-0 mb-4 holidays-container-left">
                    <div className="container">
                        <SectionHeader />
                    </div>
                </div>
                {items.length > 0 && (
                    <div className="row">
                        {items.map((item, i) => (
                            <div className="col-lg-2 col-md-3 col-6 mb-4" key={item.id || i}>
                                <a href={item.link_url || '#'} rel="noreferrer">
                                    {item.image_url && (
                                        <div className="image-container">
                                            <img
                                                className="br-15 top-destination-img-h"
                                                src={item.image_url}
                                                alt={item.title || ''}
                                                width="100%"
                                            />
                                        </div>
                                    )}
                                    {item.title && <h4 className="country-name-destination">{item.title}</h4>}
                                    {item.subtitle && <p className="text-muted small">{item.subtitle}</p>}
                                </a>
                            </div>
                        ))}
                    </div>
                )}
                <SectionCTA />
            </div>
        );
    }

    /* ─── IMAGE SLIDER / DESTINATION SLIDER ─── */
    if (displayType === 'image_slider' || displayType === 'destination_slider' || displayType === 'cab_slider') {
        return (
            <div className="container swiper pb-5 holidays-left">
                <div className="popular-state pb-0 pt-0 mb-4 holidays-container-left">
                    <div className="container">
                        <SectionHeader />
                    </div>
                </div>
                {items.length > 0 && (
                    <Swiper
                        modules={[Navigation, Autoplay]}
                        navigation
                        autoplay={{ delay: 3000, disableOnInteraction: false }}
                        slidesPerView={1}
                        spaceBetween={15}
                        loop={items.length > 4}
                        breakpoints={{
                            320: { slidesPerView: 2, spaceBetween: 10 },
                            640: { slidesPerView: 3, spaceBetween: 15 },
                            1024: { slidesPerView: section.items_per_view || 4, spaceBetween: 15 },
                            1440: { slidesPerView: section.items_per_view || 5, spaceBetween: 15 },
                        }}
                    >
                        {items.map((item, i) => (
                            <SwiperSlide key={item.id || i}>
                                <a href={item.link_url || '#'} className="card-link" rel="noreferrer">
                                    <div className="card swipper-holidays" style={{ border: 'none' }}>
                                        {item.image_url && (
                                            <div className="card-image">
                                                <img
                                                    src={item.image_url}
                                                    alt={item.title || ''}
                                                    className="card-img internation-dest-img-sec"
                                                />
                                            </div>
                                        )}
                                        {item.title && (
                                            <div className="card-content">
                                                <h2 className="properties-title">
                                                    <span className="international-country-name">{item.title}</span>
                                                </h2>
                                            </div>
                                        )}
                                        {item.subtitle && <p className="text-muted small px-2 pb-2">{item.subtitle}</p>}
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
                <SectionCTA />
            </div>
        );
    }

    /* ─── OFFER CARDS / PACKAGE CARDS ─── */
    if (displayType === 'offer_cards' || displayType === 'package_cards') {
        return (
            <div className="container pb-5">
                <div className="popular-state pb-0 pt-0 mb-4">
                    <SectionHeader />
                </div>
                <div className="row">
                    {items.map((item, i) => (
                        <div className="col-lg-4 col-md-6 mb-4" key={item.id || i}>
                            <a href={item.link_url || '#'} rel="noreferrer" style={{ textDecoration: 'none' }}>
                                <div className="team-item" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                                    {item.image_url && (
                                        <div className="team-image">
                                            <figure>
                                                <img src={item.image_url} alt={item.title || ''} style={{ width: '100%', objectFit: 'cover' }} />
                                            </figure>
                                        </div>
                                    )}
                                    <div className="team-content" style={{ padding: '12px' }}>
                                        {item.badge_text && (
                                            <span className="badge bg-warning text-dark mb-1">{item.badge_text}</span>
                                        )}
                                        {item.title && <h3><a className="text-white" href={item.link_url || '#'}>{item.title}</a></h3>}
                                        {item.subtitle && <p className="text-white mb-0">{item.subtitle}</p>}
                                        {item.price_text && <p className="text-warning fw-bold mb-0">{item.price_text}</p>}
                                        {item.button_text && (
                                            <a href={item.link_url || '#'} className="btn btn-sm btn-light mt-2">{item.button_text}</a>
                                        )}
                                    </div>
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
                <SectionCTA />
            </div>
        );
    }

    /* ─── BENEFITS ─── */
    if (displayType === 'benefits') {
        return (
            <div className="container pb-5">
                <div className="popular-state pb-0 pt-0 mb-4">
                    <SectionHeader />
                </div>
                <div className="row justify-content-center">
                    {items.map((item, i) => (
                        <div className="col-lg-3 col-md-4 col-6 text-center mb-4" key={item.id || i}>
                            {item.image_url && (
                                <img src={item.image_url} alt={item.title || ''} style={{ width: '60px', height: '60px', objectFit: 'contain', marginBottom: '10px' }} />
                            )}
                            {item.title && <h5 style={{ fontWeight: 600 }}>{item.title}</h5>}
                            {item.subtitle && <p className="text-muted small">{item.subtitle}</p>}
                        </div>
                    ))}
                </div>
                <SectionCTA />
            </div>
        );
    }

    /* ─── BANNER / HERO ─── */
    if (displayType === 'banner' || displayType === 'hero') {
        const bannerItem = items[0] || {};
        const bgImg = bannerItem.image_url || bgImage;
        return (
            <div
                className="mb-5"
                style={{
                    backgroundImage: bgImg ? `url(${bgImg})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    minHeight: '250px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                    margin: '0 16px 32px',
                    padding: '40px 32px',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {bgImg && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', borderRadius: '12px' }} />
                )}
                <div style={{ position: 'relative', zIndex: 1 }}>
                    {(titleHighlight || title) && (
                        <h2 style={{ color: '#fff', fontWeight: 700 }}>
                            {titleHighlight && <span style={{ color: '#ffd700' }}>{titleHighlight} </span>}
                            {title}
                        </h2>
                    )}
                    {subtitle && <p style={{ color: '#eee', fontSize: '1.1rem' }}>{subtitle}</p>}
                    {description && <p style={{ color: '#ddd' }}>{description}</p>}
                    {section.button_text && (
                        <a href={section.button_link || '#'} className="btn btn-warning fw-semibold">
                            {section.button_text}
                        </a>
                    )}
                </div>
            </div>
        );
    }

    /* ─── MULTI-BANNERS ─── */
    if (displayType === 'banners') {
        return (
            <div className="container pb-5">
                <SectionHeader />
                <div className="row">
                    {items.map((item, i) => (
                        <div className="col-md-6 col-lg-4 mb-3" key={item.id || i}>
                            <a href={item.link_url || '#'} rel="noreferrer">
                                <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
                                    {item.image_url && (
                                        <img src={item.image_url} alt={item.title || ''} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                                    )}
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                                        {item.title && <h4 style={{ color: '#fff', margin: 0 }}>{item.title}</h4>}
                                        {item.subtitle && <p style={{ color: '#ddd', margin: 0, fontSize: '0.85rem' }}>{item.subtitle}</p>}
                                    </div>
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
                <SectionCTA />
            </div>
        );
    }

    /* ─── LISTING HEADING ─── */
    if (displayType === 'listing_heading') {
        return (
            <div className="container pb-4 text-center">
                <SectionHeader />
                {section.button_text && (
                    <a href={section.button_link || '#'} className="btn btn-primary">
                        {section.button_text}
                    </a>
                )}
            </div>
        );
    }

    /* ─── CAB ROUTES ─── */
    if (displayType === 'cab_routes') {
        return (
            <div className="container pb-5">
                <div className="popular-state pb-0 pt-0 mb-4">
                    <SectionHeader />
                </div>
                <div className="row">
                    {items.map((item, i) => (
                        <div className="col-lg-3 col-md-4 col-6 mb-3" key={item.id || i}>
                            <a href={item.link_url || '#'} rel="noreferrer" style={{ textDecoration: 'none' }}>
                                <div style={{ border: '1px solid #eee', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                                    {item.image_url && (
                                        <img src={item.image_url} alt={item.title || ''} style={{ width: '48px', marginBottom: '8px' }} />
                                    )}
                                    {item.title && <h5 style={{ fontWeight: 600 }}>{item.title}</h5>}
                                    {item.subtitle && <p className="text-muted small mb-0">{item.subtitle}</p>}
                                    {item.price_text && <p style={{ color: '#e67e22', fontWeight: 700 }}>{item.price_text}</p>}
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
                <SectionCTA />
            </div>
        );
    }

    /* ─── DEFAULT FALLBACK ─── */
    return (
        <div className="container pb-5">
            <div className="popular-state pb-0 pt-0 mb-4">
                <SectionHeader />
            </div>
            {items.length > 0 && (
                <div className="row">
                    {items.map((item, i) => (
                        <div className="col-lg-3 col-md-4 col-6 mb-4" key={item.id || i}>
                            <a href={item.link_url || '#'} rel="noreferrer" style={{ textDecoration: 'none' }}>
                                <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #eee' }}>
                                    {item.image_url && (
                                        <img src={item.image_url} alt={item.title || ''} style={{ width: '100%', objectFit: 'cover', height: '160px' }} />
                                    )}
                                    <div style={{ padding: '12px' }}>
                                        {item.badge_text && <span className="badge bg-warning text-dark mb-1">{item.badge_text}</span>}
                                        {item.title && <h5 style={{ fontWeight: 600 }}>{item.title}</h5>}
                                        {item.subtitle && <p className="text-muted small mb-1">{item.subtitle}</p>}
                                        {item.price_text && <p style={{ color: '#e67e22', fontWeight: 700 }}>{item.price_text}</p>}
                                        {item.button_text && (
                                            <a href={item.link_url || '#'} className="btn btn-sm btn-outline-primary mt-1">{item.button_text}</a>
                                        )}
                                    </div>
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
            )}
            <SectionCTA />
        </div>
    );
};

export default GenericDynamicSection;
